import { Container } from 'pixi.js';
import { PathSection } from '../../../element-editor/shape/shapes/path-shape/path-sections/path-section';
import {
  ControlPointDefinition,
  Coords,
  DragConstraintInstance,
  ShapeSelectParams,
} from '../../resource/types/shape.type';
import { GeneralShape } from '../../../element-editor/shape/shapes/general/general-shape';
import { Circle } from '../../../element-editor/shape/shapes/primitive/circle-element';
import { RectangleController } from '../rectangle-controller/rectangle-controller';
import { isEqual } from 'lodash';
import { bulkUpdateFinish } from '../../../element-editor/store/editor.actions';
import { HandSectionNext } from '../../../element-editor/shape/shapes/path-shape/hand-shape-next/hand-section-next';
import { BaseVector } from '../../base/vector/vector';
import { StudioSceneShape } from '../../../element-editor/shape/shapes/general/root/studio-scene-shape';
import { StudioTextShape } from '../../../element-editor/store/studio/text/studio-text-shape';
import { StudioSceneShapeCell } from '../../../element-editor/shape/shapes/general/root/studio-scene-shape-cell';
import { StudioShape } from '../../../element-editor/store/studio/shapes/studio-shape';

const { PI, sqrt, pow } = Math;
const between = (a: number, a1: number, a2: number) => a1 < a && a < a2;

export interface DragResponse {
  x?: number;
  y?: number;
  dx?: number;
  dy?: number;
  d?: number;
  pc?: PointController;
}

export interface PointControllerConfig {
  pOffset?: Coords;
  dOffset?: number;
  angle?: number;
  d?: number;
  ds?: number;
  de?: number;
  p?: Coords;
  ps?: Coords;
  pe?: Coords;
  noScale?: boolean;
  noConstraint?: boolean;

  fix?: 'length' | 'angle';
  showCircle?: boolean;
  start?: () => void;
  drag?: (resp: DragResponse) => void;
  baseDrag?: (dx: number, dy: number) => void;
  end?: (params?: {
    dx?: number;
    dy?: number;
    draggedOnto?: ControlPointDefinition;
  }) => void;
  click?: () => void;
  dragOnto?: (params: ControlPointDefinition) => void;
  mouseover?: () => void;
  mouseout?: () => void;
  disableActions?: boolean;
  updateCircle?: boolean;
  controlPoint?: ControlPointDefinition;
  hack?: boolean;
  color?: string;
  noStroke?: boolean;
  cursorType?: string;

  fill?: string;
  'stroke-width'?: number;
  rotation?: number;
  rotateAround?: {
    l: number;
    angle: number;
  };
}

export class PointController {
  steep: boolean;
  m: number;
  quarter: number;

  // circle: Element;
  // hoverCircle: Element;
  unMovedP = [0, 0];
  unMovedD = 0;

  inProgress = false;

  IRI: string;

  get pathSectionParent() {
    return this.parent as PathSection;
  }

  get canvasScale() {
    return this.config.noScale ? 1 : this.cs.canvasScale;
  }

  get cs() {
    return this.parent.cs;
  }

  get r() {
    return 4 / this.cs.canvasScale;
  }

  get rL() {
    return 5 / this.canvasScale;
  }

  get r2() {
    return 10 / this.canvasScale;
  }

  get strokeWidth() {
    if (this.config.noStroke) {
      return 0;
    }
    return 0.75 / this.canvasScale;
  }

  get _strokeWidth() {
    return this.config['stroke-width'] / this.canvasScale;
  }

  get rotation() {
    return this.config.rotation;
  }

  get fix() {
    return this.config.fix;
  }

  get ps() {
    return this.config.ps;
  }

  get pe() {
    return this.config.pe;
  }

  get xy() {
    const [xOffset, yOffset] = this.pOffset;
    const [x, y] = this.p;

    let [cx, cy] = [xOffset + x, yOffset + y];

    if (this.angle != undefined) {
      this.quarter = this.getQuarter(this.angle);
      this.m = this.getLineM(this.angle);
      this.steep = this.m < -1 || 1 < this.m;

      const offset = this.d + this.dOffset;
      const [dcx, dcy] = [
        offset * Math.cos(this.angle),
        offset * Math.sin(this.angle),
      ];

      cx += dcx;
      cy += dcy;
      // this.m = undefined;
    }

    return [cx || 0, cy || 0];
  }

  get angle() {
    return this.norm(this.config.angle);
  }

  get controlPoint() {
    return this.config.controlPoint;
  }

  get dOffset() {
    return this.config.dOffset || 0;
  }

  get d() {
    return this.config.d || 0;
  }

  get p() {
    return this.config.p || [0, 0];
  }

  get pOffset() {
    return this.config.pOffset || [0, 0];
  }

  set d(val: number) {
    this.config.d = val;
  }

  get xs() {
    if (this.config.angle && this.config.ds) {
      return Math.cos(this.config.ds);
    } else {
      return this.config.ps[0]; //- this.config.p?.[1] || 0;
    }
  }

  get ys() {
    if (this.config.angle && this.config.ds) {
      return Math.sin(this.config.ds);
    } else {
      return this.config.ps[1]; //- this.config.p?.[1] || 0;
    }
  }

  get xe() {
    return this.config.pe[0];
  }

  get ye() {
    return this.config.pe[1];
  }

  get circleAttrs() {
    return {
      r: this.r,
      // stroke: this.config.color || '#',
      'stroke-width': this.strokeWidth,
    };
  }

  get hoverCircleAttrs() {
    return {
      r: this.r2,
      'stroke-width': this.strokeWidth,
    };
  }

  // TODO - remove
  get id() {
    return (this.parent as PathSection).id;
  }

  get shape():
    | GeneralShape
    | StudioShape
    | StudioSceneShape
    | StudioSceneShapeCell {
    if (this.parent instanceof PathSection) {
      return this.parent.pathShape;
    }

    if (this.parent instanceof RectangleController) {
      return this.parent.parent;
    }

    if (this.parent instanceof HandSectionNext) {
      return this.parent.handShape;
    }
    // -- // -- //
    return this.parent;
  }

  get shapeService() {
    return this.shape.service;
  }

  circle: Circle;
  hoverCircle: Circle;

  constructor(
    public readonly parent:
      | PathSection
      | RectangleController
      | GeneralShape
      | HandSectionNext,
    // TODO - move to private  again
    public config: PointControllerConfig,
    private container?: Container,
    private auxContainer?: Container,
  ) {
    this.unMovedD = this.d;

    this.container ||= (parent as GeneralShape)?.circleContainer;
    this.auxContainer ||= (parent as GeneralShape)?.auxCircleContainer;

    this.IRI = Math.random().toString();

    this.initCircles();

    if (this.angle != undefined) {
      this.quarter = this.getQuarter(this.angle);
    }
  }

  mouseIsOver = false;

  stopDrag() {
    this.hoverCircle.stopDrag();
  }

  initCircles() {
    const [cx, cy] = this.xy;
    this.circle = new Circle(this, this.container, {
      x: cx,
      y: cy,
      r: this.r,
      stroke: this.config.color || '#000',
      fill: this.config.fill || '#ffffff',
    });

    // Hovercircle is invisible and slightly larger then the main circle.
    // It makes to more convenient to drag the point-controller.
    this.hoverCircle = new Circle(this, this.container, {
      x: cx,
      y: cy,
      r: this.r2,
      // fill: '#00ff00',
      // opacity: 0.001,
      // stroke: '#ff0000',
    })
      .drag(
        (dx, dy) => this.drag(dx, dy),
        () => this.startDrag(),
        () => this.endDrag(),
      )

      .mouseover(() => {
        this.cs._hoveredControlPoint = this;
        this.mouseIsOver = true;
        if (this.inProgress) {
          return;
        }
        this.config.mouseover?.();

        if (this.parent instanceof PathSection) {
          this.cs.hoveredPoint = this.parent;
        }

        if (this.config.controlPoint) {
          this.cs.hoveredControlPoint = this.config.controlPoint;
          // console.log(
          //   'cs.hoveredControlPoint',
          //   this.cs.hoveredControlPoint.absCoords
          // );
        }
        this.circle.patch({ r: this.rL });

        // Set resize cursor when hovering over point controller
        this.hoverCircle.element.cursor = this.config.cursorType || 'move';
      })
      .mouseout(() => {
        this.cs._hoveredControlPoint = null;
        this.mouseIsOver = false;
        this.config.mouseout?.();

        if (this.inProgress) {
          return;
        }
        this.cs.hoveredPoint = null;
        this.cs.hoveredControlPoint = null;
        this.circle.patch({ r: this.r });

        // Reset cursor when leaving point controller
        this.hoverCircle.element.cursor = 'default';
      })
      .click(() => this.config.click?.());
  }

  zoomUpdate() {
    this.circle.patch(this.circleAttrs);
    this.hoverCircle.patch(this.hoverCircleAttrs);
  }

  baseDragMode = false;

  draggedOnto(params: ControlPointDefinition) {
    if (isEqual(params, this.config.controlPoint)) {
      return;
    }

    this.config.dragOnto?.(params);
  }

  currentConstraints: DragConstraintInstance[] = [];

  circleConstraint: {
    x: number;
    y: number;
    l: number;
    external?: boolean;
  };

  currentConstraint: DragConstraintInstance;
  dragBaseAngle: number;
  dragBaseUnmoved: Coords;

  startDrag() {
    // console.log('sd - 1', this.unMovedP);
    this.inProgress = true;
    this.cs.draggedPointController = this;

    this.container.removeChild(this.circle.element);
    this.container.removeChild(this.hoverCircle.element);

    this.auxContainer.addChild(this.circle.element);
    this.auxContainer.addChild(this.hoverCircle.element);
    // if (this.fix == 'length') {
    //   this.circleConstraint = {
    //     x: this.x,
    //     y: this.y,
    //     l: sqrt(pow(this.x, 2) + pow(this.y, 2)),
    //   };
    // } else {
    //   this.circleConstraint = null;
    // }

    // if (this.config.noConstraint) {
    //   this.currentConstraints = [];
    // } else if (this.controlPoint?.name) {
    //   this.currentConstraints =
    //     (this._parent as GeneralShape).startDragBy?.(
    //       this.controlPoint?.name,
    //       {},
    //       { direct: true },
    //     ) || [];
    // }

    this.currentConstraint = this.controlPointParent?._startDragBy(
      this.controlPoint?.name,
      {},
      { direct: true },
    );
    // console.log('current-constraint', this.currentConstraint);
    if (this.currentConstraint) {
      if (!this.currentConstraint.limit) {
        this.currentConstraint.limit = this.currentConstraint.constraints?.find(
          ({ limit }) => !!limit,
        )?.limit;
      }
      this.circleConstraint = this.currentConstraint.limit;
      // -- // -- //
    }

    this.unMovedP = this.xy;

    if (this.circleConstraint) {
      this.dragBaseAngle = new BaseVector([
        this.circleConstraint.x,
        this.circleConstraint.y,
      ]).getAngle();
    }
    // TOOD - check if it is not needed
    // this.disableHover();
    if (this.config.disableActions) {
      return;
    }
    if (this.angle !== undefined) {
      this.unMovedD = this.d;
      this.quarter = this.getQuarter(this.angle);
    } else {
      this.unMovedP = this.config.p;
    }
    if (this.config.p) {
      this.unMovedP = this.config.p;
    }
    if (this.shape.cs.isPressed('v')) {
      // this.shape.localStartDrag();
      this.baseDragMode = true;
    } else {
      this.config.start?.();
    }
  }

  get _parent() {
    return (this.shapeService.getShapeByIRI(this.controlPoint?.shapeIRI) ||
      this.parent) as GeneralShape;
  }

  get controlPointParent() {
    return this.shapeService.getShapeByIRI(this.controlPoint?.shapeIRI);
  }
  dx: number;
  dy: number;
  drag(dx: number, dy: number) {
    if (this.baseDragMode) {
      return this.shapeService.drag(dx, dy);
    }

    if (this.config.baseDrag) {
      return this.config.baseDrag(dx, dy);
    }

    const resp = this.dragHandler([dx, dy]);

    this.dx = resp.dx;
    this.dy = resp.dy;

    /* if (this.controlPoint.shapeIRI) {
      this.shapeService
        .getShapeByIRI(this.controlPoint.shapeIRI)
        .dragNew([this.dx, this.dy]);
    } */

    const [x, y] = this.pOffset;
    const [x0, y0] = this.p;

    // -- // -- //
    let dAngle = 0;
    if (this.circleConstraint) {
      const [x, y] = this.unMovedP;

      const currentAngle = new BaseVector([
        this.circleConstraint.x + this.dx,
        this.circleConstraint.y + this.dy,
      ]).getAngle();

      dAngle = currentAngle - this.dragBaseAngle;
      // console.log('dragBaseAngle', this.dragBaseAngle);
      // console.log('dAngle', dAngle, 'currentAngle', currentAngle);
    }

    if (this.currentConstraint) {
      this.controlPointParent._dragBy(
        this.currentConstraint,
        {
          dx: this.dx,
          dy: this.dy,
          dAngle,
        },
        true,
      );
    }

    // this.circle.patch({
    // x: x + resp.x,
    // y: y + resp.y,
    //   x: x + x0 + resp.dx,
    //   y: y + y0 + resp.dy,
    // });
    // this.hoverCircle.patch({
    //   x: x + x0 + resp.dx,
    //   y: y + y0 + resp.dy,
    //   // x: x + resp.x,
    //   // y: y + resp.y,
    // });
    this.config.drag?.(resp);
  }
  interrupted = false;
  interrupt() {
    this.interrupted = true;
  }
  endDrag() {
    if (this.interrupted) {
      return;
    }

    this.auxContainer.removeChild(this.circle.element);
    this.auxContainer.removeChild(this.hoverCircle.element);

    this.container.addChild(this.circle.element);
    this.container.addChild(this.hoverCircle.element);

    if (this.baseDragMode) {
      this.baseDragMode = false;
      return this.shapeService.endDrag();
    }

    // this.currentConstraints.map(({ shape, point }) =>
    //   shape.endDragBy(point, [this.dx, this.dy]),
    // );

    const draggedOnto = this.checkNewConstraint();

    this.circleConstraint = null;

    this.inProgress = false;
    this.unMovedP = this.xy;
    this.unMovedD = this.d;

    this.config.end?.({ dx: this.dx, dy: this.dy, draggedOnto });
    this.circle.patch({ r: this.r });

    if (this.currentConstraint) {
      this.controlPointParent._endDragBy(this.currentConstraint);
    }

    this.parent.cs.store.dispatch(bulkUpdateFinish());
  }

  checkNewConstraint() {
    let draggedOnto: ControlPointDefinition;
    if (this.controlPoint) {
      if (
        this.cs.hoveredControlPoint &&
        this.cs.hoveredControlPoint.shapeIRI !== this.controlPoint.shapeIRI &&
        !this.cs.currentAnimationID
      ) {
        draggedOnto = this.cs.hoveredControlPoint;
        const { shapeIRI, name: hoveredPointId } = this.cs.hoveredControlPoint;

        if (
          this.currentConstraint.constraints.find(
            c => c.point == hoveredPointId,
          )
        ) {
          return;
        }

        const hoveredShape = this.shapeService.getResource(
          shapeIRI,
        ) as GeneralShape;

        const [hoveredX, hoveredY] =
          hoveredShape.getAbsCoordsOfControlPoint(hoveredPointId);
        const parent = this.shapeService.getShapeByIRI(
          this.controlPoint.shapeIRI,
        );

        const [absX, absY] = parent.getAbsCoordsOfControlPoint(
          this.controlPoint.name,
        );

        // const [absX, absY] = [parent.x + this.p[0], parent.y + this.p[1]];

        // console.log('constraint', { x: parent.x, y: parent.y, p: this.p });
        // console.log({ absX, absY });

        const dx = hoveredX - absX;
        const dy = hoveredY - absY;

        parent.addConstraintByTarget(
          this.controlPoint.name,
          this.cs.hoveredControlPoint,
        );
        hoveredShape.addConstraintByTarget(
          this.cs.hoveredControlPoint.name,
          this.controlPoint,
        );

        this.drag(dx, dy);

        return draggedOnto;
      }
    }
  }

  _redrawCircle() {
    const [x, y] = this.xy;
    this.hoverCircle.patch({ x, y, r: this.r2 });
    // this.hoverCircle.patch({ x, y });
  }

  norm(angle: number) {
    if (angle > 2 * PI) {
      return angle - 2 * PI;
    }
    if (angle < 0) {
      return angle + 2 * PI;
    }
    return angle;
  }

  remove() {
    // TODO - check if nullish coelescing is necessary here
    this.circle.remove();
    this.hoverCircle.remove();
  }

  getLineM(angle: number) {
    const m = Math.tan(this.flipAngle(angle));
    return isNaN(m) ? Infinity : m;
  }

  getQuarter(angle: number) {
    return Math.floor(this.flipAngle(angle) / (PI / 2));
  }

  flipAngle(angle: number) {
    return angle;

    if (between(angle, 0, PI / 2) || between(angle, (3 * PI) / 2, 2 * PI)) {
      return 2 * PI - angle;
    }
    if (between(angle, PI / 2, (3 * PI) / 2)) {
      return PI + (PI - angle);
    }
  }

  dragHandler(coords: Coords): DragResponse {
    let [dx, dy] = coords;
    if (this.circleConstraint) {
      const { x: baseX, y: baseY, l, external } = this.circleConstraint;
      const newX = baseX + dx;
      const newY = baseY + dy;
      let angle = Math.atan(newY / newX);

      if (newX < 0) {
        if (newY >= 0) {
          angle += Math.PI;
        } else {
          angle -= Math.PI;
        }
      }

      const [x, y] = [Math.cos(angle) * l, Math.sin(angle) * l];
      // console.log('external', { x, y }); // -- //
      // if (external || true) {
      [dx, dy] = [x - baseX, y - baseY];
      return {
        x: dx,
        y: dy,
        dx: dx,
        dy: dy,
        pc: this,
      };
      // } else {
      //   return {
      //     x,
      //     y,
      //     dx: newX - x,
      //     dy: newY - y,
      //     pc: this,
      //   };
      // }
    }

    if (this.rotation) {
      let angle = Math.atan(dy / dx);

      if (dx < 0) {
        if (dy >= 0) {
          angle += Math.PI;
        } else {
          angle -= Math.PI;
        }
      }

      const diffAngle = angle - this.rotation;
      const l = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));

      return {
        dx: Math.cos(diffAngle) * l,
        dy: Math.sin(diffAngle) * l,
        pc: this,
      };
    }

    [dx, dy] = this.limitDiff(dx, dy);
    // console.log('unMoved-p', this.controlPoint);
    const [x0, y0] = this.unMovedP;
    // dx /= this.canvasScale;
    // dy /= this.canvasScale;
    const [x, y] = [x0 + dx, y0 + dy];
    if (this.config.updateCircle) {
      this.updateCircles(x, y);
    }
    if (this.angle !== undefined) {
      const d = this.getD(dx, dy);
      // if (this.ps || this.pe) {
      //   [dx, dy] = this.limitByPoint(dx, dy);
      // }
      return {
        x,
        y,
        d: this.unMovedD + (this.config.hack ? -d : d),
        dx,
        dy,
        // pc: this,
      };
    } else {
      return { x, y, d: 0, dx, dy };
    }
  }

  updateCircles(cx: number, cy: number) {
    this.circle.patch({ x: cx, y: cy });
    this.hoverCircle.patch({ x: cx, y: cy });
  }

  getD(x: number, y: number) {
    const d = sqrt(pow(x, 2) + pow(y, 2));
    if (this.quarter === 0 || this.quarter === 1) {
      return Math.sign(y) * d;
    } else {
      return -Math.sign(y) * d;
    }
  }

  limitDiff(x: number, y: number) {
    [x, y] = this.limitByLength(x, y);
    [x, y] = this.limitByLine(x, y);
    return [x, y];
  }

  get x() {
    if (this.config.angle !== undefined && this.unMovedD !== 0) {
      return this.unMovedD * Math.cos(this.config.angle);
    } else {
      return this.config.p[0];
    }
  }

  get y() {
    if (this.config.angle !== undefined && this.unMovedD) {
      return this.unMovedD * Math.sin(this.config.angle);
    } else {
      return this.config.p[1];
    }
  }

  limitByLength(dx: number, dy: number) {
    if (this.config.ds !== undefined) {
      const dDmax = this.unMovedD - this.config.ds;

      switch (this.quarter) {
        case 0:
          dx = Math.max(-dDmax * Math.cos(this.config.angle), dx);
          dy = Math.max(dDmax * Math.sin(this.config.angle), dy);
          break;
        case 1:
          // dx goes negative -> min limits it
          dx = Math.min(-dDmax * Math.cos(this.config.angle), dx);
          // dy goes positive -> max limits it
          dy = Math.max(-dDmax * Math.sin(this.config.angle), dy);
          // -- // -- // -- //
          break;
        case 2:
          dx = Math.min(-dDmax * Math.cos(this.config.angle), dx);
          dy = Math.max(dDmax * Math.sin(this.config.angle), dy);
          break;
        case 3:
          // dx goes positive -> min limits | cos(q4) is positive
          dx = Math.max(-dDmax * Math.cos(this.config.angle), dx);
          // dy goes negative -> max limit | sin(q4) is negative
          dy = Math.min(-dDmax * Math.sin(this.config.angle), dy);
          break;
      }

      // if (this.quarter === 0 || this.quarter === 3) {
      //   dx = Math.max(-d * Math.cos(this.config.angle), dx);
      // } else {
      //   dx = Math.min(-d * Math.cos(this.config.angle), dx);
      // }

      // if (this.quarter === 0 || this.quarter === 1) {
      //   dy = Math.max(d * Math.sin(this.config.angle), dy);
      // } else {
      //   dy = Math.min(d * Math.sin(this.config.angle), dy);
      // }
    }

    if (this.config.de !== undefined) {
      const d = this.config.de - this.unMovedD;
      switch (this.quarter) {
        case 0:
          dx = Math.min(d * Math.cos(this.config.angle), dx);
          dy = Math.min(-d * Math.sin(this.config.angle), dy);
          break;
        case 1:
          // dx goes negative -> max limits
          dx = Math.max(d * Math.cos(this.config.angle), dx);
          // dy goes positive -> min limits it
          dy = Math.min(d * Math.sin(this.config.angle), dy);
          // -- // -- // -- //
          break;
        case 2:
          dx = Math.max(d * Math.cos(this.config.angle), dx);
          dy = Math.max(-d * Math.sin(this.config.angle), dy);
          break;
        case 3:
          // dx goes positive -> min limtits
          dx = Math.min(d * Math.cos(this.config.angle), dx);
          // dy goes negative --> max limit
          dy = Math.max(d * Math.sin(this.config.angle), dy);
          break;
      }

      // console.log('dx', dx, 'dy', dy);
      //   if (this.quarter === 0 || this.quarter === 3) {
      //     dx = Math.min(d * Math.cos(this.config.angle), dx);
      //   } else {
      //     dx = Math.max(d * Math.cos(this.config.angle), dx);
      //   }

      //   if (this.quarter === 0 || this.quarter === 1) {
      //     dy = Math.min(-d * Math.sin(this.config.angle), dy);
      //   } else {
      //     dy = Math.max(-d * Math.sin(this.config.angle), dy);
      //   }
    }

    return [dx, dy];
  }

  limitByLine(dx: number, dy: number) {
    if (this.m !== undefined) {
      //console.log('limit-by-line', this.m);
      if (this.m === Infinity) {
        dy = 0;
      } else if (this.steep) {
        dx = this.getX(dy);
      } else {
        dy = this.getY(dx);
      }
    }
    return [dx, dy];
  }

  limitByPoint(dx: number, dy: number) {
    const [x, y] = this.unMovedP;
    if (this.config.ps) {
      if (this.quarter === 0 || this.quarter === 3) {
        dx = Math.max(this.xs - x, dx);
      } else {
        dx = Math.min(this.xs - x, dx);
      }
      if (this.quarter === 0 || this.quarter === 1) {
        dy = Math.max(this.ys - y, dy);
      } else {
        dy = Math.min(this.ys - y, dy);
      }
    }

    if (this.config.pe) {
      if (this.quarter === 0 || this.quarter === 3) {
        dx = Math.min(this.xe - x, dx);
      } else {
        dx = Math.max(this.xe - x, dx);
      }
      if (this.quarter === 0 || this.quarter === 1) {
        dy = Math.min(this.ye - y, dy);
      } else {
        dy = Math.max(this.ye - y, dy);
      }
    }
    return [dx, dy];
  }

  clearAngle() {
    this.config.angle = 0;
  }

  getX(y: number) {
    return y / this.m;
  }

  getY(x: number) {
    return this.m * x;
  }

  delete(keys: string[]) {
    keys.map(key => delete this.config[key]);
  }

  patch(config: Partial<PointControllerConfig>, log = false) {
    this.config = {
      ...this.config,
      ...config,
    };
    this.refresh(log);

    if (log) {
      // console.log('p', config.p); //
    }
  }

  clear(config: Partial<PointControllerConfig>) {
    Object.keys(config).map(key => delete this.config[key]);
    console.log('clear', this.config);
    console.log('clear', this.angle);
    if (this.angle == undefined) {
      this.m = undefined;
    }
    this.refresh();
  }

  refresh(log = false) {
    const [cx, cy] = this.xy;
    if (log) {
      // console.log('-----pc', this.id, { cx, cy });
    }
    this.circle.patch({
      x: cx,
      y: cy,
      r: this.r,
      'stroke-width': this._strokeWidth || this.strokeWidth,
      stroke: this.config?.color || '#000',
    });
    this._redrawCircle();
    // TODD - check what that is
    if (!this.inProgress) {
      // this.circle.attr({ r: this.r }); //
      // this.hoverCircle.attr({ r: this.r2 }); //
    }
  }

  disableHover() {
    this.hoverCircle.disableHover();
  }

  enableHover() {
    this.hoverCircle.enableHover();
  }

  hide(force = false) {
    this.circle.hide();
    // if (force) {
    this.hoverCircle.hide();
    // }
  }

  show(params: ShapeSelectParams = {}) {
    this.zoomUpdate();
    this.circle.show();
    this.hoverCircle.show();
  }

  selectMany() {
    // TODO - migrate to pixi
    // this.circle?.attr({ stroke: 'red' });
  }

  deselectMany() {
    // TODO - migrate to pixi
    // this.circle?.attr({ stroke: 'black' });
  }
}

// this.circle = this.cs.snap?.circle(cx, cy, this.r).attr({
//   fill: 'white',
//   stroke: 'black',
//   visibility: this.config.showCircle ? 'visible' : 'hidden',
//   'stroke-width': this.strokeWidth,
// });

// this.hoverCircle = this.cs.snap
//   ?.circle(cx, cy, this.r2)
//   .attr({
//     opacity: 0.001,
//     visibility: this.config.showCircle ? 'visible' : 'hidden',
//   })
//   .mouseover(() => {
//     if (this.inProgress) {
//       return;
//     }

//     if (this.parent instanceof PathSection) {
//       this.cs.hoveredPoint = this.parent;
//     }

//     if (this.config.controlPoint) {
//       this.cs.hoveredControlPoint = this.config.controlPoint;
//       // console.log(
//       //   'cs.hoveredControlPoint',
//       //   this.cs.hoveredControlPoint.absCoords
//       // );
//     }

//     this.circle.attr({ r: this.rL });
//   })
//   .mouseout(() => {
//     this.cs.hoveredPoint = null;
//     this.cs.hoveredControlPoint = null;
//     this.circle.attr({ r: this.r });
//   });

// if (!this.config.showCircle) {
//   this.hide();
// }

// .mouseover(() => {
//   this.circle.attr({ r: this.rL });
// })
// .mouseout(() => {
//   if (this.inProgress) {
//     return;
//   }
//   this.circle.attr({ r: this.r });
// });

// This does not fire
// this.hoverCircle.click(e => {
//   console.log('hoverCircle-click');
//   e.stopPropagation();
// });

// this.hoverCircle?.drag(
//   (dx: number, dy: number) => {
//     if (this.config.disableActions) {
//       return;
//     }
//     this.drag(dx, dy);
//   },
//   () => {
//     this.inProgress = true;
//     this.disableHover();
//     if (this.config.disableActions) {
//       return;
//     }
//     if (this.config.a) {
//       this.unMovedD = this.d;
//     } else {
//       this.unMovedP = this.config.p;
//     }
//     if (this.config.p) {
//       this.unMovedP = this.config.p;
//     }

//     this.config.start?.();
//   },
//   e => {
//     // this stands for e.stopPropagation(); //
//     this.cs.clickAbsorbed = true;
//     this.inProgress = false;
//     this.enableHover();
//     if (this.config.disableActions) {
//       return;
//     }
//     this.config.end?.(e);
//   }
// );

// group?.add(this.circle);
// group?.add(this.hoverCircle);
